<template>
  <div class="w-full flex flex-col gap-6 justify-start acc">
    <!-- Header -->
    <div class="flex justify-between items-center">
      <div class="text-white">
        <p class="font-bold text-2xl">//{{ activeTab.title }}</p>
        <p>{{ activeTab.blurb }}</p>
      </div>
    </div>

    <!-- Three separate questions, kept separate: what am I using, what am I paying for,
         and what else is on offer. -->
    <nav class="acc-tabs" role="tablist">
      <button
        v-for="t in tabs"
        :key="t.key"
        type="button"
        role="tab"
        class="acc-tab"
        :class="{ on: t.key === tab }"
        :aria-selected="t.key === tab ? 'true' : 'false'"
        @click="goTab(t.key)"
      >
        {{ t.label }}
      </button>
    </nav>

    <div v-if="loading" class="acc-grid">
      <div class="acc-card acc-skel" />
      <div class="acc-card acc-skel" />
    </div>

    <div v-else-if="error" class="acc-card">
      <p class="acc-err">{{ error }}</p>
      <button class="acc-btn" @click="load(true)">Try again</button>
    </div>

    <template v-else-if="acc">
      <!-- USAGE -->
      <div v-if="tab === 'usage'" class="acc-grid">
        <div class="acc-card">
          <div class="acc-card-top">
            <span class="acc-label">THIS MONTH</span>
            <span v-if="resetLine" class="acc-reset">{{ resetLine }}</span>
          </div>

          <div class="acc-meter">
            <div class="acc-meter-top">
              <span>Storage</span>
              <span class="acc-num">
                <b>{{ acc.storage.usedLabel }}</b> of
                {{ acc.storage.allowedLabel }}
              </span>
            </div>
            <div class="acc-track">
              <span
                :class="acc.storage.tone"
                :style="{ width: acc.storage.pct + '%' }"
              ></span>
            </div>
          </div>

          <div class="acc-meter">
            <div class="acc-meter-top">
              <span>AI video hours</span>
              <span class="acc-num">
                <b>{{ acc.ai.usedLabel }}</b> of {{ acc.ai.allowedLabel }}
              </span>
            </div>
            <div class="acc-track">
              <span
                :class="acc.ai.tone"
                :style="{ width: acc.ai.pct + '%' }"
              ></span>
            </div>
          </div>

          <p class="acc-foot">
            Storage counts every recording your account holds. Deleting one
            frees it straight away. Live sessions, joining, watching and
            downloading are never affected by a limit.
          </p>
        </div>

        <div class="acc-card">
          <div class="acc-card-top">
            <span class="acc-label"
              >INCLUDED ON {{ acc.planName.toUpperCase() }}</span
            >
          </div>
          <div class="acc-facts">
            <div>
              <b>{{ acc.storage.allowedLabel }}</b>
              <span>secure storage</span>
            </div>
            <div>
              <b>{{ acc.ai.allowedLabel }}</b>
              <span>AI video per month</span>
            </div>
            <div>
              <b>{{ acc.dids }}</b>
              <span>{{
                acc.dids === 1
                  ? 'decentralized identity'
                  : 'decentralized identities'
              }}</span>
            </div>
            <div>
              <b>{{ acc.credentials }}</b>
              <span>Trusted Credentials</span>
            </div>
          </div>
          <div class="acc-actions">
            <button class="acc-btn" @click="goTab('plans')">
              Compare plans
            </button>
          </div>
        </div>
      </div>

      <!-- SUBSCRIPTION -->
      <div v-else-if="tab === 'subscription'" class="acc-grid">
        <div class="acc-card">
          <div class="acc-card-top">
            <span class="acc-label">YOUR PLAN</span>
            <span class="acc-pill" :class="statusTone">{{ statusLabel }}</span>
          </div>
          <h3 class="acc-plan">{{ acc.planName }}</h3>
          <p class="acc-sub">{{ planLine }}</p>
          <p v-if="renewLine" class="acc-sub">{{ renewLine }}</p>

          <div class="acc-actions">
            <button
              v-if="acc.paying"
              class="acc-btn primary"
              :disabled="busy"
              @click="manageBilling"
            >
              {{ busy ? 'Opening…' : 'Manage billing' }}
            </button>
            <button class="acc-btn" @click="goTab('plans')">
              {{ acc.paying ? 'Change plan' : 'See plans' }}
            </button>
          </div>
          <p v-if="portalErr" class="acc-err">{{ portalErr }}</p>
        </div>

        <div class="acc-card">
          <div class="acc-card-top">
            <span class="acc-label">BILLING</span>
          </div>
          <p v-if="acc.paying" class="acc-sub">
            Invoices, card details, and cancelling all live in the billing
            portal. Cancelling keeps your plan until
            {{
              renewLine
                ? renewLine.replace('Renews ', '').replace('.', '')
                : 'the end of the period'
            }}, and your media stays available afterwards either way.
          </p>
          <p v-else class="acc-sub">
            Nothing to bill: you are on the free plan. Everything you record is
            yours to keep, whatever you do next.
          </p>
          <div v-if="acc.grace" class="acc-note">
            Your account is over its storage limit. New recordings pause on
            {{ graceDate }} unless you free space or move to a larger plan.
          </div>
        </div>
      </div>

      <!-- PLANS -->
      <div v-else class="acc-plans">
        <PlanCards
          v-if="plans.length"
          :plans="plans"
          :topups="topups"
          :icons="icons"
          :yearly="yearly"
          :yearly-months="yearlyMonths"
          :signed-in="true"
          :current-tier="acc.tier"
          :highlight="highlight"
          :busy="checkoutBusy"
          free-label="Included in your account"
          @update:yearly="setYearly"
          @subscribe="subscribe"
        />
        <p v-if="checkoutErr" class="acc-err" role="alert">{{ checkoutErr }}</p>
        <p class="acc-foot">
          Prices in euro, excluding VAT.
          <a href="/pricing" target="_blank" rel="noopener"
            >Full pricing details →</a
          >
        </p>
      </div>
    </template>
  </div>
</template>

<script>
import {
  accountSummary,
  clearAccountCache,
  currentAccount,
  formatDate,
  subscribeAccount,
} from '@/http/requests/account';
import {
  fetchCatalog,
  openPortal,
  startCheckout,
  errorMessage,
  PENDING_CHECKOUT_KEY,
} from '@/http/requests/billing';
import {
  ICONS,
  PLANS,
  TOPUPS,
  YEARLY_MONTHS,
  fromCatalog,
} from '@/views/pricing/pricingData';
import PlanCards from '@/views/pricing/components/PlanCards.vue';
import { planStatus } from './copy';

export default {
  name: 'AccountSection',
  components: { PlanCards },
  data() {
    return {
      acc: currentAccount(),
      loading: true,
      error: '',
      busy: false,
      portalErr: '',
      // the catalogue, with the bundled copy standing in until it answers
      plans: PLANS,
      topups: TOPUPS,
      icons: ICONS,
      yearlyMonths: YEARLY_MONTHS,
      yearly: localStorage.getItem('planInterval') === 'year',
      checkoutBusy: '',
      checkoutErr: '',
      // the plan they arrived wanting, marked so it is obvious which one they picked
      highlight: '',
      tabs: [
        {
          key: 'usage',
          label: 'Usage',
          title: 'Usage',
          blurb: 'What your account is holding and spending this month.',
        },
        {
          key: 'subscription',
          label: 'Subscription',
          title: 'Subscription',
          blurb: 'The plan you are on, and where billing is handled.',
        },
        {
          key: 'plans',
          label: 'Plans',
          title: 'Plans',
          blurb: 'What each plan includes, and what it costs.',
        },
      ],
    };
  },
  computed: {
    /** Which tab is showing. The route owns it so the page can be linked to. */
    tab() {
      const asked = this.$route.params.tab;
      return this.tabs.some((t) => t.key === asked) ? asked : 'usage';
    },
    activeTab() {
      return this.tabs.find((t) => t.key === this.tab) || this.tabs[0];
    },
    graceDate() {
      const grace = this.acc && this.acc.grace;
      return grace ? formatDate(grace.deadline) : '';
    },
    statusLabel() {
      return planStatus(this.acc).label;
    },
    statusTone() {
      return planStatus(this.acc).tone;
    },
    planLine() {
      return planStatus(this.acc).line;
    },
    renewLine() {
      if (!this.acc.paying || !this.acc.periodEnd) return '';
      return `Renews ${formatDate(this.acc.periodEnd)}.`;
    },
    resetLine() {
      if (!this.acc.periodEnd) return '';
      return `AI hours reset ${formatDate(this.acc.periodEnd)}`;
    },
  },
  created() {
    document.title = 'Plan & usage · Decast';
    this.unsubscribe = subscribeAccount((acc) => {
      if (acc) this.acc = acc;
    });
    this.load();
    this.loadCatalog();
    // a plan bought or changed in the Stripe portal lands back here
    if (this.$route.query.from === 'portal') this.load(true);
    // Either the person picked a plan on the marketing site, or they picked one here,
    // were sent to sign in, and are now back. Both end in the same place: that checkout.
    const asked = this.$route.query.plan;
    const pending = localStorage.getItem(PENDING_CHECKOUT_KEY);
    const planId = this.normalisePlanId(asked) || pending;
    if (planId) {
      this.yearly = /-year$/.test(planId);
      this.resumeCheckout(planId);
    }
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
    if (this._retry) clearTimeout(this._retry);
  },
  methods: {
    loadCatalog() {
      fetchCatalog()
        .then((c) => {
          const m = fromCatalog(c);
          if (!m) return;
          this.plans = m.plans;
          this.topups = m.topups;
          this.yearlyMonths = m.yearlyMonths;
        })
        .catch(() => {});
    },
    setYearly(value) {
      this.yearly = value;
      try {
        localStorage.setItem('planInterval', value ? 'year' : 'month');
      } catch (e) {
        // remembering the choice is a convenience, not a requirement
      }
    },
    /**
     * A plan the account is already on opens billing; a different one starts a checkout.
     * Stripe refuses a second subscription, so a subscriber changing plan goes to the
     * portal, which is also where proration and cancellation live.
     */
    subscribe(plan) {
      if (!this.acc) return;
      if (this.acc.paying) {
        this.manageBilling();
        return;
      }
      const interval = this.yearly ? 'year' : 'month';
      const ref = plan.planRefs && plan.planRefs[interval];
      this.checkout((ref && ref.planId) || `${plan.key}-${interval}`);
    },
    checkout(planId) {
      this.checkoutBusy = planId.split('-')[0];
      this.checkoutErr = '';
      startCheckout(planId)
        .then((session) => {
          // it exists now, so nothing needs to remember it any more
          localStorage.removeItem(PENDING_CHECKOUT_KEY);
          window.location.assign(session.url);
        })
        .catch((e) => {
          const status = e && e.response && e.response.status;
          if (status === 409) {
            this.manageBilling();
            return;
          }
          if (status === 401) {
            localStorage.setItem(PENDING_CHECKOUT_KEY, planId);
            this.$router.push('/v2login');
            return;
          }
          this.checkoutBusy = '';
          this.checkoutErr = errorMessage(
            e,
            'Checkout could not be started. Try again in a moment.'
          );
        });
    },
    /**
     * One failure is usually a token being refreshed underneath us on a cold load, so try
     * again quietly before telling anyone. Only a second failure is worth a message.
     */
    load(force = false, attempt = 0) {
      this.loading = true;
      this.error = '';
      if (force) clearAccountCache();
      accountSummary(force)
        .then((a) => {
          this.acc = a;
          this.loading = false;
        })
        .catch(() => {
          if (attempt < 1) {
            clearAccountCache();
            this._retry = setTimeout(() => this.load(true, attempt + 1), 1200);
            return;
          }
          this.error = 'Your plan could not be loaded. Try again in a moment.';
          this.loading = false;
        });
    },
    /**
     * A plan named in a link, in whatever shape it arrives: `sensitive`, `sensitive-month`
     * or `sensitive-year`. An interval in the query wins over one in the id.
     */
    normalisePlanId(value) {
      if (!value || typeof value !== 'string') return '';
      const tier = value.split('-')[0].toLowerCase();
      // 'enterprise' stays accepted: links shared before the tier was renamed
      // Structured still arrive with it
      if (!['sensitive', 'structured', 'enterprise'].includes(tier)) return '';
      const asked = String(this.$route.query.interval || '').toLowerCase();
      const interval =
        asked === 'year' || asked === 'month'
          ? asked
          : /-year$/.test(value)
          ? 'year'
          : 'month';
      return `${tier}-${interval}`;
    },
    /**
     * A plan chosen on the marketing site, or before signing in. Open the plans page with
     * it picked out and the right billing period showing, and leave the decision to them:
     * being thrown straight at a payment form is startling, and they may want to read the
     * comparison first now that they are here.
     */
    resumeCheckout(planId) {
      localStorage.removeItem(PENDING_CHECKOUT_KEY);
      this.highlight = planId.split('-')[0];
      this.goTab('plans');
      this.$nextTick(() => {
        const el = this.$el.querySelector('.acc-plans');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    goTab(key) {
      if (key === this.tab) return;
      const path =
        key === 'usage' ? '/dashboard/account' : `/dashboard/account/${key}`;
      this.$router.push(path);
    },
    manageBilling() {
      this.busy = true;
      this.portalErr = '';
      openPortal('/dashboard/account?from=portal')
        .then((session) => {
          window.location.assign(session.url);
        })
        .catch((e) => {
          this.busy = false;
          this.portalErr = errorMessage(
            e,
            'The billing portal is unavailable right now.'
          );
        });
    },
  },
};
</script>

<style scoped>
.acc * {
  font-family: 'JetBrains Mono', monospace !important;
}
.acc-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}
@media (min-width: 768px) {
  .acc-grid {
    grid-template-columns: 1fr 1fr;
  }
  .acc-span {
    grid-column: 1 / -1;
  }
}
.acc-card {
  border-top: 1px solid #ffffff;
  border-left: 1px solid #ffffff;
  border-right: 3px solid #ffffff;
  border-bottom: 3px solid #ffffff;
  background: #0a0b0f;
  padding: 1.15rem 1.25rem;
  color: #fff;
}
.acc-skel {
  height: 190px;
  opacity: 0.35;
}
.acc-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 0.85rem;
}
.acc-label {
  font-size: 12px;
  letter-spacing: 0.1em;
  color: #8a8a8a;
}
.acc-reset {
  font-size: 12px;
  color: #8a8a8a;
}
.acc-pill {
  font-size: 11.5px;
  padding: 3px 10px;
  border-radius: 20px;
}
.acc-pill.good {
  color: #22c55e;
  background: #22c55e20;
}
.acc-pill.bad {
  color: #ef4444;
  background: #ef444420;
}
.acc-pill.neutral {
  color: #d7df23;
  background: #d7df2320;
}
.acc-plan {
  font-size: 1.6rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 0.35rem;
}
.acc-sub {
  font-size: 13.5px;
  color: #b4b4b4;
  margin: 0 0 0.2rem;
}
.acc-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 1rem;
}
.acc-btn {
  border: 1px solid #ffffff59;
  background: transparent;
  color: #fff;
  padding: 7px 14px;
  font-size: 13px;
  cursor: pointer;
}
.acc-btn:hover {
  border-color: #fff;
}
.acc-btn.primary {
  background: #d7df23;
  border-color: #d7df23;
  color: #0a0b0f;
  font-weight: 700;
}
.acc-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.acc-meter + .acc-meter {
  margin-top: 1rem;
}
.acc-meter-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: 13.5px;
  color: #b4b4b4;
  margin-bottom: 7px;
}
.acc-num b {
  color: #fff;
}
.acc-track {
  height: 8px;
  background: #ffffff1a;
  overflow: hidden;
}
.acc-track span {
  display: block;
  height: 100%;
  background: #d7df23;
  transition: width 0.3s;
}
.acc-track span.warn {
  background: #f0c45a;
}
.acc-track span.over {
  background: #ef4444;
}
.acc-foot {
  margin: 1rem 0 0;
  font-size: 12px;
  color: #6f6f6f;
}
.acc-facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem 1.5rem;
}
@media (min-width: 768px) {
  .acc-facts {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
.acc-facts div {
  display: flex;
  flex-direction: column;
}
.acc-facts b {
  color: #d7df23;
  font-size: 1.15rem;
}
.acc-facts span {
  color: #b4b4b4;
  font-size: 12.5px;
}
.acc-tabs {
  display: flex;
  gap: 2px;
  border-bottom: 1px solid #ffffff1f;
}
.acc-tab {
  background: none;
  border: 0;
  border-bottom: 2px solid transparent;
  color: #8a8a8a;
  padding: 8px 16px;
  font-size: 13px;
  letter-spacing: 0.04em;
  cursor: pointer;
  font-family: 'JetBrains Mono', monospace !important;
}
.acc-tab:hover {
  color: #fff;
}
.acc-tab.on {
  color: #d7df23;
  border-bottom-color: #d7df23;
}
.acc-note {
  margin-top: 0.9rem;
  border-left: 3px solid #f0c45a;
  background: #f0c45a14;
  color: #f4e2b0;
  padding: 0.7rem 0.9rem;
  font-size: 12.5px;
}
.acc-plans {
  margin-top: 0.5rem;
}
.acc-plans .acc-foot a {
  color: #d7df23;
}
.acc-plans-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 1rem;
  color: #fff;
}
.acc-link {
  font-size: 13px;
  color: #d7df23;
}
.acc-link:hover {
  text-decoration: underline;
}
.acc-err {
  color: #ef4444;
  font-size: 13px;
  margin: 0.6rem 0 0;
}
</style>
